import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Table,
  Spinner,
  Alert,
} from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import api from "../../api";

// Log API initialization
console.log(
  "SubscriptionMetricsDashboard: API initialized with baseURL:",
  api.defaults.baseURL
);

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Simple formatters to avoid import error
const formatCurrency = (value, currency = "SGD") => {
  return new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2,
  }).format(value);
};

const formatNumber = (value) => {
  return new Intl.NumberFormat("en-SG").format(value);
};

const formatPercent = (value) => {
  return new Intl.NumberFormat("en-SG", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
};

// API functions
const getSubscriptionMetricsDashboard = async (days = 30) => {
  try {
    console.log(
      `SubscriptionMetricsDashboard: Fetching metrics for ${days} days`
    );
    const response = await api.get(
      `/api/admin/metrics/dashboard/?days=${days}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching subscription metrics:", error);
    throw error;
  }
};

const getSubscriptionMetricsExportUrl = (days = 30) => {
  // Use the current API base URL
  const baseURL = api.defaults.baseURL || "";
  return `${baseURL}/api/admin/metrics/export/?days=${days}`;
};

const SubscriptionMetricsDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchMetrics();
  }, [days]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSubscriptionMetricsDashboard(days);
      setMetrics(data);
    } catch (err) {
      console.error("Error fetching subscription metrics:", err);
      setError(err.message || "Failed to load subscription metrics");
    } finally {
      setLoading(false);
    }
  };

  const handleDaysChange = (e) => {
    setDays(parseInt(e.target.value, 10));
  };

  const handleExport = () => {
    const exportUrl = getSubscriptionMetricsExportUrl(days);
    window.open(exportUrl, "_blank");
  };

  // Prepare chart data for MRR
  const prepareChartData = (label, data, color) => {
    if (!metrics || !metrics.trends || !metrics.trends.dates) return null;

    return {
      labels: metrics.trends.dates,
      datasets: [
        {
          label,
          data,
          borderColor: color,
          backgroundColor: `${color}33`, // Add transparency
          tension: 0.2,
          fill: false,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" variant="primary" />
        <h3 className="mt-3">Loading subscription metrics...</h3>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Metrics</Alert.Heading>
          <p>{error}</p>
          <div className="d-flex justify-content-end">
            <Button variant="outline-danger" onClick={fetchMetrics}>
              Retry
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Subscription Metrics Dashboard</h2>
        <div className="d-flex align-items-center">
          <Form.Select
            value={days}
            onChange={handleDaysChange}
            className="me-2"
            style={{ width: "150px" }}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last year</option>
          </Form.Select>
          <Button variant="outline-primary" onClick={handleExport}>
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {metrics && metrics.summary && (
        <>
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center h-100">
                <Card.Body>
                  <Card.Title>Active Subscriptions</Card.Title>
                  <h3 className="my-3">
                    {metrics.summary.total_active_subscriptions}
                  </h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center h-100">
                <Card.Body>
                  <Card.Title>Monthly Revenue</Card.Title>
                  <h3 className="my-3">
                    {formatCurrency(
                      metrics.summary.monthly_recurring_revenue,
                      "SGD"
                    )}
                  </h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center h-100">
                <Card.Body>
                  <Card.Title>Trial Conversion</Card.Title>
                  <h3 className="my-3">
                    {metrics.summary.trial_conversion_rate}%
                  </h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center h-100">
                <Card.Body>
                  <Card.Title>Churn Rate</Card.Title>
                  <h3 className="my-3">
                    {metrics.summary.monthly_churn_rate}%
                  </h3>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Charts */}
          {metrics.trends && metrics.trends.dates && (
            <>
              <Row className="mb-4">
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <Card.Title>Monthly Recurring Revenue</Card.Title>
                      <Line
                        data={prepareChartData(
                          "MRR (SGD)",
                          metrics.trends.monthly_recurring_revenue,
                          "#28a745"
                        )}
                        options={chartOptions}
                      />
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <Card.Title>Active Subscriptions</Card.Title>
                      <Line
                        data={prepareChartData(
                          "Active Subscriptions",
                          metrics.trends.active_subscriptions,
                          "#007bff"
                        )}
                        options={chartOptions}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <Card.Title>Trials & Conversions</Card.Title>
                      <Line
                        data={{
                          labels: metrics.trends.dates,
                          datasets: [
                            {
                              label: "New Trials",
                              data: metrics.trends.new_trials,
                              borderColor: "#17a2b8",
                              backgroundColor: "#17a2b833",
                              tension: 0.2,
                              fill: false,
                            },
                            {
                              label: "Trial Conversions",
                              data: metrics.trends.trial_conversions,
                              borderColor: "#ffc107",
                              backgroundColor: "#ffc10733",
                              tension: 0.2,
                              fill: false,
                            },
                          ],
                        }}
                        options={chartOptions}
                      />
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <Card.Title>Churn Rate</Card.Title>
                      <Line
                        data={prepareChartData(
                          "Churn Rate (%)",
                          metrics.trends.churn_rate,
                          "#dc3545"
                        )}
                        options={chartOptions}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}

          {/* Detailed Metrics Table */}
          {metrics.detailed_metrics && (
            <Card className="mb-4">
              <Card.Body>
                <Card.Title>Detailed Metrics</Card.Title>
                <div className="table-responsive">
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>New Trials</th>
                        <th>Conversions</th>
                        <th>New Subs</th>
                        <th>Canceled</th>
                        <th>Failed Payments</th>
                        <th>Successful Payments</th>
                        <th>Active Subs</th>
                        <th>MRR (SGD)</th>
                        <th>Churn (%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.detailed_metrics.map((metric) => (
                        <tr key={metric.date}>
                          <td>{new Date(metric.date).toLocaleDateString()}</td>
                          <td>{metric.new_trials}</td>
                          <td>{metric.trial_conversions}</td>
                          <td>{metric.new_subscriptions}</td>
                          <td>{metric.canceled_subscriptions}</td>
                          <td>{metric.failed_payments}</td>
                          <td>{metric.successful_payments}</td>
                          <td>{metric.total_active_subscriptions}</td>
                          <td>
                            {formatCurrency(
                              metric.monthly_recurring_revenue,
                              "SGD"
                            )}
                          </td>
                          <td>{metric.monthly_churn_rate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}
        </>
      )}
      {!metrics && !loading && !error && (
        <Alert variant="info">
          No subscription metrics data available. Please check back later or
          contact an administrator.
        </Alert>
      )}
    </Container>
  );
};

export default SubscriptionMetricsDashboard;
